import APIService from "./APIService.js";

/**
 * SystemAnnouncementsAPI - Frontend service for managing platform system announcements
 *
 * This is separate from AlertsAPI which handles Alerts Intelligence Hub (scam education).
 * System Announcements are for:
 * - Scheduled maintenance notices
 * - New feature announcements
 * - Service status updates
 * - Platform-wide notifications
 */
class SystemAnnouncementsAPI {
  constructor() {
    this.endpoint = "/api/v1/app/system-announcements";
  }

  /**
   * Get active announcements for public display (no authentication required)
   * @returns {Promise<Array>} Array of active announcement objects
   */
  async getActiveAnnouncements() {
    try {
      console.log("📢 SystemAnnouncementsAPI: Fetching active announcements");
      const announcements = await APIService.get(`${this.endpoint}/active`);
      console.log(
        "✅ SystemAnnouncementsAPI: Retrieved",
        announcements?.length || 0,
        "active announcements",
      );
      return announcements || [];
    } catch (error) {
      console.error(
        "❌ SystemAnnouncementsAPI: Error fetching active announcements:",
        error,
      );
      return [];
    }
  }

  /**
   * Get all announcements for admin management (admin only)
   * @returns {Promise<Array>} Array of all announcements (including inactive)
   */
  async getAllAnnouncementsForAdmin() {
    try {
      console.log(
        "🔧 SystemAnnouncementsAPI: Fetching all announcements for admin",
      );
      const announcements = await APIService.get(`${this.endpoint}/admin`);
      console.log(
        "✅ SystemAnnouncementsAPI: Retrieved",
        announcements?.length || 0,
        "admin announcements",
      );
      return announcements || [];
    } catch (error) {
      console.error(
        "❌ SystemAnnouncementsAPI: Error fetching admin announcements:",
        error,
      );
      return [];
    }
  }

  /**
   * Create a new announcement (admin only)
   * @param {Object} announcementData - Announcement data object
   * @returns {Promise<Object>} Created announcement object
   */
  async createAnnouncement(announcementData) {
    try {
      console.log(
        "➕ SystemAnnouncementsAPI: Creating new announcement:",
        announcementData.message?.substring(0, 50),
      );
      const announcement = await APIService.post(
        this.endpoint,
        announcementData,
      );
      console.log(
        "✅ SystemAnnouncementsAPI: Announcement created successfully:",
        announcement,
      );
      return announcement;
    } catch (error) {
      console.error(
        "❌ SystemAnnouncementsAPI: Error creating announcement:",
        error,
      );
      throw error;
    }
  }

  /**
   * Update an existing announcement (admin only)
   * @param {number} announcementId - The ID of the announcement to update
   * @param {Object} announcementData - Updated announcement data
   * @returns {Promise<Object>} Updated announcement object
   */
  async updateAnnouncement(announcementId, announcementData) {
    try {
      console.log(
        "✏️ SystemAnnouncementsAPI: Updating announcement with ID:",
        announcementId,
      );
      const announcement = await APIService.put(
        `${this.endpoint}/${announcementId}`,
        announcementData,
      );
      console.log(
        "✅ SystemAnnouncementsAPI: Announcement updated successfully:",
        announcement,
      );
      return announcement;
    } catch (error) {
      console.error(
        "❌ SystemAnnouncementsAPI: Error updating announcement:",
        announcementId,
        error,
      );
      throw error;
    }
  }

  /**
   * Delete an announcement (admin only) - hard delete
   * @param {number} announcementId - The ID of the announcement to delete
   * @returns {Promise<void>}
   */
  async deleteAnnouncement(announcementId) {
    try {
      console.log(
        "🗑️ SystemAnnouncementsAPI: Deleting announcement with ID:",
        announcementId,
      );
      await APIService.delete(`${this.endpoint}/${announcementId}`);
      console.log(
        "✅ SystemAnnouncementsAPI: Announcement deleted successfully",
      );
    } catch (error) {
      console.error(
        "❌ SystemAnnouncementsAPI: Error deleting announcement:",
        announcementId,
        error,
      );
      throw error;
    }
  }

  /**
   * Deactivate an announcement (admin only) - soft delete
   * @param {number} announcementId - Announcement ID
   * @returns {Promise<void>}
   */
  async deactivateAnnouncement(announcementId) {
    try {
      console.log(
        `⏹️ SystemAnnouncementsAPI: Deactivating announcement ${announcementId}`,
      );
      await APIService.put(`${this.endpoint}/${announcementId}/deactivate`);
      console.log(
        "✅ SystemAnnouncementsAPI: Announcement deactivated successfully",
      );
    } catch (error) {
      console.error(
        `❌ SystemAnnouncementsAPI: Error deactivating announcement ${announcementId}:`,
        error,
      );
      throw error;
    }
  }

  /**
   * Reactivate an announcement (admin only)
   * @param {number} announcementId - Announcement ID
   * @returns {Promise<void>}
   */
  async reactivateAnnouncement(announcementId) {
    try {
      console.log(
        `▶️ SystemAnnouncementsAPI: Reactivating announcement ${announcementId}`,
      );
      await APIService.put(`${this.endpoint}/${announcementId}/reactivate`);
      console.log(
        "✅ SystemAnnouncementsAPI: Announcement reactivated successfully",
      );
    } catch (error) {
      console.error(
        `❌ SystemAnnouncementsAPI: Error reactivating announcement ${announcementId}:`,
        error,
      );
      throw error;
    }
  }

  /**
   * Get active maintenance announcements
   * @returns {Promise<Array>} Array of maintenance announcements
   */
  async getMaintenanceAnnouncements() {
    try {
      console.log(
        "🔧 SystemAnnouncementsAPI: Fetching maintenance announcements",
      );
      const announcements = await APIService.get(
        `${this.endpoint}/maintenance`,
      );
      console.log(
        "✅ SystemAnnouncementsAPI: Retrieved",
        announcements?.length || 0,
        "maintenance announcements",
      );
      return announcements || [];
    } catch (error) {
      console.error(
        "❌ SystemAnnouncementsAPI: Error fetching maintenance announcements:",
        error,
      );
      return [];
    }
  }

  /**
   * Get count of active announcements
   * @returns {Promise<number>} Count of active announcements
   */
  async getAnnouncementCount() {
    try {
      const response = await APIService.get(`${this.endpoint}/count`);
      return response?.count || 0;
    } catch (error) {
      console.error(
        "❌ SystemAnnouncementsAPI: Error getting announcement count:",
        error,
      );
      return 0;
    }
  }
}

// Export a singleton instance
const systemAnnouncementsAPI = new SystemAnnouncementsAPI();
export default systemAnnouncementsAPI;
